import { __extends, __assign } from 'tslib';
import * as React from 'react';
import { Presence } from './types.js';
import { layoutStack } from './utils/stack.js';
import { SharedLayoutContext } from '../../context/SharedLayoutContext.js';
import { MotionContext } from '../../context/MotionContext/index.js';
import { resetRotate } from './utils/rotate.js';
import { createBatcher } from './utils/batcher.js';
import { snapshotViewportBox } from '../../render/dom/projection/utils.js';
/**
 * @public
 */

var AnimateSharedLayout =
/** @class */
function (_super) {
  __extends(AnimateSharedLayout, _super);

  function AnimateSharedLayout() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    /**
     * A list of all the children in the shared layout
     */


    _this.children = new Set();
    /**
     * As animate components with a defined `layoutId` are added/removed to the tree,
     * we store them in order. When one is added, it will animate out from the
     * previous one, and when it's removed, it'll animate to the previous one.
     */

    _this.stacks = new Map();
    /**
     * Track whether the component has mounted. If it hasn't, the presence of added children
     * are set to Present, whereas if it has they're considered Entering
     */

    _this.hasMounted = false;
    /**
     * Track whether we already have an update scheduled. If we don't, we'll run snapshots
     * and schedule one.
     */

    _this.updateScheduled = false;
    /**
     * Tracks whether we already have a render scheduled. If we don't, we'll force one with this.forceRender
     */

    _this.renderScheduled = false;
    /**
     * The methods provided to all children in the shared layout tree.
     */

    _this.syncContext = __assign(__assign({}, createBatcher()), {
      syncUpdate: function syncUpdate(force) {
        return _this.scheduleUpdate(force);
      },
      forceUpdate: function forceUpdate() {
        // By copying syncContext to itself, when this component re-renders it'll also re-render
        // all children subscribed to the SharedLayout context.
        _this.syncContext = __assign({}, _this.syncContext);

        _this.scheduleUpdate(true);
      },
      register: function register(child) {
        return _this.addChild(child);
      },
      remove: function remove(child) {
        return _this.removeChild(child);
      }
    });
    return _this;
  }

  AnimateSharedLayout.prototype.componentDidMount = function () {
    this.hasMounted = true;
  };

  AnimateSharedLayout.prototype.componentDidUpdate = function () {
    this.startLayoutAnimation();
  };

  AnimateSharedLayout.prototype.shouldComponentUpdate = function () {
    this.renderScheduled = true;
    return true;
  };

  AnimateSharedLayout.prototype.startLayoutAnimation = function () {
    var _this = this;
    /**
     * Reset update and render scheduled status
     */


    this.renderScheduled = this.updateScheduled = false;
    var type = this.props.type;
    /**
     * Update presence metadata based on the latest AnimatePresence status.
     * This is a kind of goofy way of dealing with this, perhaps there's a better model to find.
     */

    this.children.forEach(function (child) {
      if (!child.isPresent) {
        child.presence = Presence.Exiting;
      } else if (child.presence !== Presence.Entering) {
        child.presence = child.presence === Presence.Exiting ? Presence.Entering : Presence.Present;
      }
    });
    this.updateStacks();
    /**
     * Create a handler which we can use to flush the children animations
     */

    var handler = {
      layoutReady: function layoutReady(child) {
        if (child.getLayoutId() !== undefined) {
          var stack = _this.getStack(child);

          stack.animate(child, type === "crossfade");
        } else {
          child.notifyLayoutReady();
        }
      },
      parent: this.context.visualElement
    };
    /**
     * Shared layout animations can be used without the AnimateSharedLayout wrapping component.
     * This requires some co-ordination across components to stop layout thrashing
     * and ensure measurements are taken at the correct time.
     *
     * Here we use that same mechanism of schedule/flush.
     */

    this.children.forEach(function (child) {
      return _this.syncContext.add(child);
    });
    this.syncContext.flush(handler);
    /**
     * Clear snapshots so subsequent rerenders don't retain memory of outgoing components
     */

    this.stacks.forEach(function (stack) {
      return stack.clearSnapshot();
    });
  };

  AnimateSharedLayout.prototype.updateStacks = function () {
    this.stacks.forEach(function (stack) {
      return stack.updateLeadAndFollow();
    });
  };

  AnimateSharedLayout.prototype.scheduleUpdate = function (force) {
    if (force === void 0) {
      force = false;
    }

    if (!(force || !this.updateScheduled)) return;
    /**
     * Flag we've scheduled an update
     */

    this.updateScheduled = true;
    /**
     * Write: Reset transforms so bounding boxes can be accurately measured.
     */

    this.children.forEach(function (child) {
      resetRotate(child);
      if (child.shouldResetTransform()) child.resetTransform();
    });
    /**
     * Read: Snapshot children
     */

    this.children.forEach(snapshotViewportBox);
    /**
     * Every child keeps a local snapshot, but we also want to record
     * snapshots of the visible children as, if they're are being removed
     * in this render, we can still access them.
     *
     * TODO: What would be better here is doing a single loop where we
     * only snapshotViewportBoxes of undefined layoutIds and then one for each stack
     */

    this.stacks.forEach(function (stack) {
      return stack.updateSnapshot();
    });
    /**
     * Force a rerender by setting state if we aren't already going to render.
     */

    if (force || !this.renderScheduled) {
      this.renderScheduled = true;
      this.forceUpdate();
    }
  };

  AnimateSharedLayout.prototype.addChild = function (child) {
    this.children.add(child);
    this.addToStack(child);
    child.presence = this.hasMounted ? Presence.Entering : Presence.Present;
  };

  AnimateSharedLayout.prototype.removeChild = function (child) {
    this.scheduleUpdate();
    this.children.delete(child);
    this.removeFromStack(child);
  };

  AnimateSharedLayout.prototype.addToStack = function (child) {
    var stack = this.getStack(child);
    stack === null || stack === void 0 ? void 0 : stack.add(child);
  };

  AnimateSharedLayout.prototype.removeFromStack = function (child) {
    var stack = this.getStack(child);
    stack === null || stack === void 0 ? void 0 : stack.remove(child);
  };
  /**
   * Return a stack of animate children based on the provided layoutId.
   * Will create a stack if none currently exists with that layoutId.
   */


  AnimateSharedLayout.prototype.getStack = function (child) {
    var id = child.getLayoutId();
    if (id === undefined) return; // Create stack if it doesn't already exist

    !this.stacks.has(id) && this.stacks.set(id, layoutStack());
    return this.stacks.get(id);
  };

  AnimateSharedLayout.prototype.render = function () {
    return React.createElement(SharedLayoutContext.Provider, {
      value: this.syncContext
    }, this.props.children);
  };

  AnimateSharedLayout.contextType = MotionContext;
  return AnimateSharedLayout;
}(React.Component);

export { AnimateSharedLayout };